import { useEffect, useState } from 'react';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { getPhonePlaceholder, hasBusinessStructure } from '../lib/regionConfig';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Illustration } from '../components/Illustration';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Plus, Loader2, Pencil, Trash2, Users, Mail, Phone, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { CREW_KINDS, crewKind } from '../lib/crewKinds';
import { PageToolbar, STICKY_TABLE, stickyTableHeader } from '../components/PageToolbar';

// One empty form for both a new record and a reset. A function, so each
// caller gets its own nested objects.
const emptyCrewForm = (kind = 'crew') => ({
  kind,
  name: '',
  email: '',
  phone: '',
  role: '',
  date_of_birth: '',
  address: '',
  tfn: '',
  default_day_rate: '',
  day_type: '10hr',
  default_markup_percent: '',
  requires_super: false,
  super_details: {
    fund_name: '',
    member_number: '',
    usi: '',
  },
  insurance: {
    expiry_date: '',
    policy_number: '',
    coverage_type: '',
  },
  abn: '',
  business_name: '',
  diet: '',
  coffee_order: '',
  default_equipment: [],
});

export default function CrewPage() {
  const { crewMembers, crewMembersLoading, fetchCrewMembers, createCrewMember, updateCrewMember, deleteCrewMember } = useAppStore();
  const { hasProjectsPlan, isAdmin, isDemo, checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();
  const { region, regionConfig, formatCurrency } = useRegion();
  // Super, TFN and USI are Australian payroll fields
  const showSuperFields = hasBusinessStructure(region);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');

  useEffect(() => {
    if (!projectsPlanChecked) {
      checkProjectsPlanAccess();
    }
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  const [formData, setFormData] = useState(() => emptyCrewForm());

  useEffect(() => {
    fetchCrewMembers();
  }, [fetchCrewMembers]);

  const resetForm = () => {
    setFormData(emptyCrewForm());
    setEditingCrew(null);
  };

  const openNewDialog = () => {
    resetForm();
    setFormData(emptyCrewForm(kindFilter === 'supplier' ? 'supplier' : 'crew'));
    setShowDialog(true);
  };

  const openEditDialog = (crew) => {
    setEditingCrew(crew);
    setFormData({
      kind: crewKind(crew),
      name: crew.name || '',
      email: crew.email || '',
      phone: crew.phone || '',
      role: crew.role || '',
      date_of_birth: crew.date_of_birth || '',
      address: crew.address || '',
      tfn: crew.tfn || '',
      default_day_rate: crew.default_day_rate || '',
      day_type: crew.day_type || '10hr',
      default_markup_percent: crew.default_markup_percent || '',
      requires_super: crew.requires_super || false,
      super_details: crew.super_details || {
        fund_name: '',
        member_number: '',
        usi: '',
      },
      insurance: crew.insurance || {
        expiry_date: '',
        policy_number: '',
        coverage_type: '',
      },
      abn: crew.abn || '',
      business_name: crew.business_name || '',
      diet: crew.diet || '',
      coffee_order: crew.coffee_order || '',
      default_equipment: crew.default_equipment || [],
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        // A supplier never takes super; the backend enforces this too.
        requires_super: formData.kind === 'supplier' ? false : formData.requires_super,
        default_day_rate: parseFloat(formData.default_day_rate) || 0,
        default_markup_percent: parseFloat(formData.default_markup_percent) || 0,
      };

      if (editingCrew) {
        await updateCrewMember(editingCrew.id, submitData);
        toast.success(`${CREW_KINDS[formData.kind].label} updated`);
      } else {
        await createCrewMember(submitData);
        toast.success(`${CREW_KINDS[formData.kind].label} created`);
      }
      setShowDialog(false);
      resetForm();
    } catch (error) {
      const noun = CREW_KINDS[formData.kind].label.toLowerCase();
      toast.error(editingCrew ? `Failed to update ${noun}` : `Failed to create ${noun}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (crew) => {
    if (!window.confirm(`Archive ${crew.name}? This won't delete their data from existing jobs.`)) {
      return;
    }

    try {
      await deleteCrewMember(crew.id);
      toast.success('Crew member archived');
    } catch (error) {
      toast.error('Failed to archive crew member');
    }
  };

  const handleUnarchive = async (crew) => {
    try {
      await updateCrewMember(crew.id, { status: 'active' });
      toast.success('Crew member restored');
    } catch (error) {
      toast.error('Failed to restore crew member');
    }
  };

  // Check if insurance is expired or expiring soon
  const getInsuranceStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'red', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', color: 'orange', days: daysUntilExpiry };
    }
    return { status: 'valid', color: 'green', days: daysUntilExpiry };
  };

  const isSupplierForm = formData.kind === 'supplier';
  const newRecordLabel = kindFilter === 'supplier' ? 'Supplier' : 'Crew Member';

  const filteredCrewMembers = crewMembers
    .filter(crew => showArchived || crew.status === 'active')
    .filter(crew => kindFilter === 'all' || crewKind(crew) === kindFilter)
    .filter(crew =>
      crew.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (crew.role && crew.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (crew.email && crew.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  if (!hasProjectsPlan && !isAdmin && !isDemo) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <h2 className="record-title">Filmmaker plan required</h2>
          <p className="text-muted-foreground">Crew / Suppliers management is available on the Filmmaker plan.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-in">
        {/* Header */}
        <PageHeader title="Crew / Suppliers" />

        {/* Tabs on the left, then search and the action on the right, as on Jobs */}
        <PageToolbar hairline={false}>
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={kindFilter} onValueChange={setKindFilter}>
              <TabsList>
                <TabsTrigger value="all" data-testid="crew-filter-all">All</TabsTrigger>
                <TabsTrigger value="crew" data-testid="crew-filter-crew">Crew</TabsTrigger>
                <TabsTrigger value="supplier" data-testid="crew-filter-supplier">Suppliers</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Switch
                checked={showArchived}
                onCheckedChange={setShowArchived}
                id="show-archived"
              />
              <Label htmlFor="show-archived" className="cursor-pointer text-sm whitespace-nowrap">Show archived</Label>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search crew and suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-11 w-full sm:w-[320px]"
              />
            </div>
            <Button onClick={openNewDialog} data-testid="add-crew-btn">
              <Plus className="w-4 h-4 mr-2" /> Add {newRecordLabel}
            </Button>
          </div>
        </PageToolbar>

        {/* Crew List */}
        {crewMembersLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredCrewMembers.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              {searchTerm
                ? <Users className="w-12 h-12 text-muted-foreground mb-4" />
                : <Illustration name="crew" className="w-full max-w-xs mb-2" />}
              <h3 className="text-lg font-medium mb-2">
                {searchTerm ? 'Nobody found' : `No ${newRecordLabel.toLowerCase()}s yet`}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {searchTerm 
                  ? 'Try adjusting your search' 
                  : `Add your first ${newRecordLabel.toLowerCase()} to get started`
                }
              </p>
              {!searchTerm && (
                <Button onClick={openNewDialog}>
                  <Plus className="w-4 h-4 mr-2" /> Add {newRecordLabel}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className={STICKY_TABLE.card}>
            <CardContent className="p-0">
              <Table containerClassName={STICKY_TABLE.container}>
                <TableHeader {...stickyTableHeader()}>
                  <TableRow>
                    <TableHead className="py-3 px-4">Name</TableHead>
                    <TableHead className="hidden md:table-cell py-3 px-4">Role</TableHead>
                    <TableHead className="hidden lg:table-cell py-3 px-4">Contact</TableHead>
                    <TableHead className="hidden xl:table-cell py-3 px-4">Day Rate</TableHead>
                    <TableHead className="hidden xl:table-cell py-3 px-4">Insurance</TableHead>
                    <TableHead className="w-[100px] py-3 px-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCrewMembers.map((crew) => {
                    const insuranceStatus = getInsuranceStatus(crew.insurance?.expiry_date);
                    return (
                      <TableRow 
                        key={crew.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openEditDialog(crew)}
                      >
                        <TableCell className="py-3 px-4">
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {crew.name}
                              {crewKind(crew) === 'supplier' && (
                                <Badge variant="secondary" className="text-xs">Supplier</Badge>
                              )}
                            </p>
                            {crew.status === 'archived' && (
                              <Badge variant="secondary" className="mt-1 text-xs">Archived</Badge>
                            )}
                            {/* Mobile: Show role and other info */}
                            <div className="md:hidden text-sm text-muted-foreground mt-1 space-y-0.5">
                              {crew.role && (
                                <p className="text-xs">{crew.role}</p>
                              )}
                              {crew.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {crew.email}
                                </span>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                {crewKind(crew) === 'crew' && (
                                  <>
                                    <span className="text-xs">{formatCurrency(Number(crew.default_day_rate) || 0)}/{crew.day_type}</span>
                                    <span className="text-xs">•</span>
                                  </>
                                )}
                                <span className="text-xs">{crew.default_markup_percent}% markup</span>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell py-3 px-4">
                          {crew.role ? (
                            <span className="text-sm">{crew.role}</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell py-3 px-4">
                          <div className="space-y-1 text-sm">
                            {crew.email && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Mail className="w-3 h-3" /> {crew.email}
                              </span>
                            )}
                            {crew.phone && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Phone className="w-3 h-3" /> {crew.phone}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell py-3 px-4">
                          <div className="text-sm">
                            {crewKind(crew) === 'supplier' ? (
                              <p className="font-medium">Kit list · {(crew.default_equipment || []).length}</p>
                            ) : (
                              <p className="font-medium">{formatCurrency(Number(crew.default_day_rate) || 0)}/{crew.day_type}</p>
                            )}
                            <p className="text-muted-foreground text-xs">{crew.default_markup_percent}% markup</p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell py-3 px-4">
                          {insuranceStatus ? (
                            <div>
                              {insuranceStatus.status === 'expired' && (
                                <Badge variant="destructive" className="flex items-center gap-1 w-fit text-xs">
                                  <AlertTriangle className="h-3 w-3" />
                                  Expired {insuranceStatus.days}d ago
                                </Badge>
                              )}
                              {insuranceStatus.status === 'expiring' && (
                                <Badge className="flex items-center gap-1 w-fit text-xs bg-ochre-soft text-ochre border-transparent">
                                  <AlertTriangle className="h-3 w-3" />
                                  Expires in {insuranceStatus.days}d
                                </Badge>
                              )}
                              {insuranceStatus.status === 'valid' && (
                                <Badge variant="secondary" className="text-xs">Valid</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(crew)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {crew.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(crew);
                                }}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnarchive(crew);
                                }}
                                className="text-muted-foreground hover:text-moss"
                                title="Restore"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCrew ? `Edit ${CREW_KINDS[formData.kind].label}` : `Add New ${CREW_KINDS[formData.kind].label}`}</DialogTitle>
            <DialogDescription>
              {isSupplierForm
                ? 'A business you hire equipment from, such as a rental house. Its kit list sets the rates on a job.'
                : editingCrew ? 'Update crew member information' : 'Add a new crew member to your team'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={formData.kind} onValueChange={(v) => setFormData((prev) => ({ ...prev, kind: v }))}>
            <TabsList>
              {Object.entries(CREW_KINDS).map(([value, { label }]) => (
                <TabsTrigger key={value} value={value} data-testid={`crew-kind-${value}`}>{label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <Tabs defaultValue="business" className="w-full pt-2">
            <TabsList>
              <TabsTrigger value="business" data-testid="crew-tab-business">Business Details</TabsTrigger>
              <TabsTrigger value="rates" data-testid="crew-tab-rates">{isSupplierForm ? 'Kit List' : 'Rates & Gear'}</TabsTrigger>
              <TabsTrigger value="insurance" data-testid="crew-tab-insurance">Insurance</TabsTrigger>
              {!isSupplierForm && (
                <TabsTrigger value="dietaries" data-testid="crew-tab-dietaries">Dietaries</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="business" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={isSupplierForm ? 'Rental house name' : 'John Smith'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">{isSupplierForm ? 'Supplies' : 'Role'}</Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder={isSupplierForm ? 'Camera, lighting, grip...' : 'Gaffer, 1st AC, Grip...'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={getPhonePlaceholder(region)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Smith Productions"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="abn">{regionConfig.businessNumber.label}</Label>
                  <Input
                    id="abn"
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                    placeholder={regionConfig.businessNumber.placeholder}
                  />
                </div>
              </div>

              {/* Superannuation - crew only, and only where the region has it */}
              {showSuperFields && !isSupplierForm && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="requires_super"
                    checked={formData.requires_super}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_super: checked })}
                  />
                  <Label htmlFor="requires_super" className="cursor-pointer">Requires Superannuation</Label>
                </div>
              )}

              {showSuperFields && !isSupplierForm && formData.requires_super && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type={formData.date_of_birth ? "date" : "text"}
                      placeholder=""
                      value={formData.date_of_birth}
                      onFocus={(e) => { e.target.type = 'date'; }}
                      onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tfn">TFN (Tax File Number)</Label>
                    <Input
                      id="tfn"
                      value={formData.tfn}
                      onChange={(e) => setFormData({ ...formData, tfn: e.target.value })}
                      placeholder="123 456 789"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main St, Sydney NSW 2000"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="super_fund_name">Super Fund Name</Label>
                    <Input
                      id="super_fund_name"
                      value={formData.super_details.fund_name}
                      onChange={(e) => setFormData({
                        ...formData,
                        super_details: { ...formData.super_details, fund_name: e.target.value }
                      })}
                      placeholder="Australian Super"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="super_member_number">Member Number</Label>
                    <Input
                      id="super_member_number"
                      value={formData.super_details.member_number}
                      onChange={(e) => setFormData({
                        ...formData,
                        super_details: { ...formData.super_details, member_number: e.target.value }
                      })}
                      placeholder="123456789"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="super_usi">USI</Label>
                    <Input
                      id="super_usi"
                      value={formData.super_details.usi}
                      onChange={(e) => setFormData({
                        ...formData,
                        super_details: { ...formData.super_details, usi: e.target.value }
                      })}
                      placeholder="12345678901234"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="rates" className="space-y-4 mt-4">
              <div className={`grid gap-4 ${isSupplierForm ? 'grid-cols-1 max-w-[200px]' : 'grid-cols-3'}`}>
                {!isSupplierForm && (
                <div className="space-y-2">
                  <Label htmlFor="default_day_rate">Default Day Rate</Label>
                  <Input
                    id="default_day_rate"
                    type="number"
                    step="0.01"
                    value={formData.default_day_rate}
                    onChange={(e) => setFormData({ ...formData, default_day_rate: e.target.value })}
                    placeholder="500.00"
                  />
                </div>
                )}
                {!isSupplierForm && (
                <div className="space-y-2">
                  <Label htmlFor="day_type">Day Type</Label>
                  <Select value={formData.day_type} onValueChange={(value) => setFormData({ ...formData, day_type: value })}>
                    <SelectTrigger id="day_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8hr">8 Hour Day</SelectItem>
                      <SelectItem value="10hr">10 Hour Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="default_markup_percent">Default Markup %</Label>
                  <Input
                    id="default_markup_percent"
                    type="number"
                    step="0.01"
                    value={formData.default_markup_percent}
                    onChange={(e) => setFormData({ ...formData, default_markup_percent: e.target.value })}
                    placeholder="20"
                  />
                </div>
              </div>

              {/* Default Equipment */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{isSupplierForm ? 'Kit List' : 'Default Equipment'}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({
                      ...formData,
                      default_equipment: [...formData.default_equipment, { id: crypto.randomUUID(), description: '', rate: '', markup_percent: formData.default_markup_percent || '' }],
                    })}
                    data-testid="add-default-equipment-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Item
                  </Button>
                </div>
                {formData.default_equipment.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
                    {isSupplierForm
                      ? 'Nothing on the kit list yet. These items and rates are added when this supplier is picked for a job.'
                      : 'No default equipment. Items added here will auto-populate when this crew member is added to a job.'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {formData.default_equipment.map((item, idx) => (
                      <Card key={item.id} className="p-3" data-testid={`default-equip-row-${idx}`}>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => {
                                const updated = [...formData.default_equipment];
                                updated[idx] = { ...updated[idx], description: e.target.value };
                                setFormData({ ...formData, default_equipment: updated });
                              }}
                              placeholder="Camera Package"
                              data-testid={`default-equip-desc-${idx}`}
                            />
                          </div>
                          <div className="w-[100px] space-y-1">
                            <Label className="text-xs">Rate</Label>
                            <Input
                              className="font-mono [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [appearance:textfield]"
                              type="number"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => {
                                const updated = [...formData.default_equipment];
                                updated[idx] = { ...updated[idx], rate: e.target.value };
                                setFormData({ ...formData, default_equipment: updated });
                              }}
                              placeholder="0.00"
                              data-testid={`default-equip-rate-${idx}`}
                            />
                          </div>
                          <div className="w-[80px] space-y-1">
                            <Label className="text-xs">Markup %</Label>
                            <Input
                              className="font-mono [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [appearance:textfield]"
                              type="number"
                              step="0.01"
                              value={item.markup_percent}
                              onChange={(e) => {
                                const updated = [...formData.default_equipment];
                                updated[idx] = { ...updated[idx], markup_percent: e.target.value };
                                setFormData({ ...formData, default_equipment: updated });
                              }}
                              placeholder="20"
                              data-testid={`default-equip-markup-${idx}`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs invisible">Del</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  default_equipment: formData.default_equipment.filter((_, i) => i !== idx),
                                });
                              }}
                              data-testid={`default-equip-delete-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="insurance" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="insurance_expiry">Insurance Expiry Date</Label>
                  <Input
                    id="insurance_expiry"
                    type={formData.insurance.expiry_date ? "date" : "text"}
                    placeholder=""
                    value={formData.insurance.expiry_date}
                    onFocus={(e) => { e.target.type = 'date'; }}
                    onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                    onChange={(e) => setFormData({
                      ...formData,
                      insurance: { ...formData.insurance, expiry_date: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="policy_number">Policy Number</Label>
                  <Input
                    id="policy_number"
                    value={formData.insurance.policy_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      insurance: { ...formData.insurance, policy_number: e.target.value }
                    })}
                    placeholder="POL-123456"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="coverage_type">Coverage Type</Label>
                  <Input
                    id="coverage_type"
                    value={formData.insurance.coverage_type}
                    onChange={(e) => setFormData({
                      ...formData,
                      insurance: { ...formData.insurance, coverage_type: e.target.value }
                    })}
                    placeholder="Public Liability, Professional Indemnity..."
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="dietaries" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="diet">Diet</Label>
                <Input
                  id="diet"
                  value={formData.diet}
                  onChange={(e) => setFormData({ ...formData, diet: e.target.value })}
                  placeholder="Vegetarian, Vegan, Gluten Free..."
                  data-testid="crew-diet-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="coffee_order">Coffee Order</Label>
                <Input
                  id="coffee_order"
                  value={formData.coffee_order}
                  onChange={(e) => setFormData({ ...formData, coffee_order: e.target.value })}
                  placeholder="Flat white, Long black, Oat latte..."
                  data-testid="crew-coffee-order-input"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <div className="flex justify-between items-center w-full">
              <div>
                {editingCrew && editingCrew.status === 'active' && (
                  <Button 
                    variant="outline" 
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDialog(false);
                      handleDelete(editingCrew);
                    }}
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                )}
                {editingCrew && editingCrew.status === 'archived' && (
                  <Button 
                    variant="outline" 
                    className="text-moss hover:text-moss"
                    onClick={(e) => {
                      e.preventDefault();
                      handleUnarchive(editingCrew);
                      setShowDialog(false);
                    }}
                    disabled={loading}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Restore
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingCrew ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
